import { lazy, Suspense, type ReactNode } from "react";
import { PortalHeading, PortalShell, type PortalNavItem } from "../../portals/shared/PortalShell";
import { SkeletonScope } from "./SkeletonScope";

// While the session is checked, draw the screen the person asked for, not a generic card: the real components in
// skeleton mode (portal shell and page, welcome, workspace picker, onboarding). None of them may redirect: pages with
// their own <Routes> get a dedicated skeleton (OnboardingSkeleton). Each page module is the same lazy
// chunk the router loads next, so nothing extra ships in the main bundle.
const WorkersPage = lazy(() => import("../../pages/workers/WorkersPage").then((m) => ({ default: m.WorkersPage })));
const ClinicalTeamPage = lazy(() => import("../../pages/clinical-team/ClinicalTeamPage").then((m) => ({ default: m.ClinicalTeamPage })));
const WelcomePage = lazy(() => import("../../pages/welcome/WelcomePage").then((m) => ({ default: m.WelcomePage })));
const SelectWorkspacePage = lazy(() => import("../../pages/select-workspace/SelectWorkspacePage").then((m) => ({ default: m.SelectWorkspacePage })));
const PrescriptionsPage = lazy(() => import("../../pages/prescriptions/PrescriptionsPage").then((m) => ({ default: m.PrescriptionsPage })));
const PrescriptionEditor = lazy(() => import("../../pages/prescriptions/PrescriptionEditor").then((m) => ({ default: m.PrescriptionEditor })));
const OnboardingSkeleton = lazy(() => import("../../pages/onboarding/OnboardingPage").then((m) => ({ default: m.OnboardingSkeleton })));

const PORTALS = {
  company: {
    label: "Portal da Empresa",
    home: { to: "/company", label: "Início", icon: "house-1", end: true },
    pages: [
      { to: "/company/trabalhadores", label: "Trabalhadores", icon: "users-two", page: () => <WorkersPage tenantId="" /> },
      { to: "/company/equipe-clinica", label: "Equipe clínica", short: "Clínica", icon: "stethoscope", page: () => <ClinicalTeamPage tenantId="" /> },
    ],
    lead: "Trabalhadores, unidades, riscos, programas e documentos liberados aparecem aqui.",
  },
  clinical: {
    label: "Portal Clínico",
    home: { to: "/clinical", label: "Início", icon: "house-1", end: true },
    pages: [
      // The editor matches first: /clinical/receitas/… draws the prescription screen, the list draws the list.
      { to: "/clinical/receitas", match: "/clinical/receitas/", label: "Receitas", icon: "prescription", page: () => <PrescriptionEditor tenantId="" /> },
      { to: "/clinical/receitas", label: "Receitas", icon: "prescription", page: () => <PrescriptionsPage tenantId="" /> },
      { to: "/clinical/equipe", label: "Equipe", icon: "users-two", page: () => <ClinicalTeamPage tenantId="" /> },
    ],
    lead: "Agenda, fila, atendimento, prontuário, exames e documentos aparecem aqui.",
  },
} as const;

function PortalSkeleton({ portal, pathname }: { portal: keyof typeof PORTALS; pathname: string }) {
  const p = PORTALS[portal];
  const current = p.pages.find((page) => pathname.startsWith("match" in page ? page.match : page.to));
  // Only the destinations we can be sure of: home and the page being opened (roles are not known yet).
  const nav: PortalNavItem[] = [p.home, ...(current ? [{ to: current.to, label: current.label, icon: current.icon, ...("short" in current ? { short: current.short } : {}) }] : [])];
  const heading = <PortalHeading title={current?.label ?? p.label} lead={current ? undefined : p.lead} />;
  return (
    <PortalShell portal={portal} label={p.label} nav={nav}>
      {current ? <Suspense fallback={heading}>{current.page()}</Suspense> : heading}
    </PortalShell>
  );
}

export function RouteSkeleton({ pathname, fallback }: { pathname: string; fallback: ReactNode }) {
  let screen: ReactNode = null;
  if (pathname.startsWith("/company")) screen = <PortalSkeleton portal="company" pathname={pathname} />;
  else if (pathname.startsWith("/clinical")) screen = <PortalSkeleton portal="clinical" pathname={pathname} />;
  else if (pathname.startsWith("/welcome")) screen = <WelcomePage />;
  else if (pathname.startsWith("/select-workspace")) screen = <SelectWorkspacePage />;
  else if (pathname.startsWith("/onboarding")) screen = <OnboardingSkeleton pathname={pathname} />;
  if (!screen) return <>{fallback}</>;
  return (
    <SkeletonScope loading label="Verificando sua sessão">
      <Suspense fallback={fallback}>{screen}</Suspense>
    </SkeletonScope>
  );
}
